import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowRight, FaBars, FaFacebookF, FaTimes } from "react-icons/fa";
import { FaTiktok, FaXTwitter, FaYoutube } from "react-icons/fa6";
import { Link } from "react-scroll";
import DocumentMeta from "./DocumentMeta";
import { ROUTE_META } from "./routeMeta";

const faqItems = [
  {
    question: "What kind of projects does Appazzola take on?",
    answer:
      "We focus on mobile apps and mobile games where clear UX, strong gameplay foundations, and practical execution matter.",
  },
  {
    question: "Do you only build new products?",
    answer:
      "No. We also maintain and improve products we have already designed and built with a client, including platform updates, store submission, and post-launch iteration.",
  },
  {
    question: "How do you approach scope?",
    answer:
      "We prefer a clear, realistic first release with room to grow, so teams can launch sooner and learn from real users.",
  },
];

const navItems = ["About", "Services", "Process", "Projects", "Contact"];

const quickPoints = [
  "Mobile-first strategy grounded in real audience and release goals",
  "UX and gameplay decisions shaped around clarity and retention",
  "Support through launch, live updates, fixes, and iteration",
];

const testimonial = {
  quote:
    "Appazzola was exceptional to work with throughout the development of our app for The Sun Newspaper in Hummelstown. They were diligent, responsive, and truly attentive to our needs at every stage of the process. Most importantly, they delivered a product that significantly streamlined the way we report incidents, improving both efficiency and accuracy. We highly recommend Appazzola to any organization looking for thoughtful, reliable app developers.",
  author: "Drew Weidman",
  role: "Editor & General Manager, The Sun",
};

const HomePage = ({
  isMenuOpen,
  toggleMenu,
  scrollY,
  appClasses,
  services,
  studioHighlights,
  processSteps,
  projectItems,
}) => (
  <div className={appClasses}>
    <DocumentMeta
      title={ROUTE_META.home.title}
      description={ROUTE_META.home.description}
      robots={ROUTE_META.home.robots}
    />

    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      <div className="orb orb-one" />
      <div className="orb orb-two" />
      <div className="grid-overlay" />
    </div>

    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed left-0 right-0 top-0 z-50 px-4 py-4 md:px-8"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between rounded-full border border-white/60 bg-white/70 px-5 py-4 shadow-[0_20px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl">
        <motion.div
          whileHover={{ scale: 1.03 }}
          className="flex items-center gap-0.5 font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl"
        >
          <img
            src="/appazzola-icon-256.png"
            alt="A"
            className="h-10 w-10 rounded-lg object-contain md:h-11 md:w-11"
          />
          <span>ppazzola</span>
        </motion.div>

        <div className="hidden items-center space-x-6 md:flex">
          {navItems.map((item) => (
            <Link
              key={item}
              to={item.toLowerCase()}
              smooth={true}
              duration={500}
              offset={-80}
              className="cursor-pointer text-sm font-semibold text-slate-700 transition-colors hover:text-cyan-700"
            >
              {item}
            </Link>
          ))}
        </div>

        <div className="flex items-center space-x-4">
          <a
            href="https://www.facebook.com/profile.php?id=61574244774078"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-slate-600 transition-colors hover:text-cyan-700"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>
          <a
            href="https://x.com/appazzola"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-slate-600 transition-colors hover:text-cyan-700"
            aria-label="X"
          >
            <FaXTwitter />
          </a>
          <a
            href="https://www.youtube.com/@appazzola"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-slate-600 transition-colors hover:text-cyan-700"
            aria-label="YouTube"
          >
            <FaYoutube />
          </a>
          <a
            href="https://www.tiktok.com/@appazzola"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg text-slate-600 transition-colors hover:text-cyan-700"
            aria-label="TikTok"
          >
            <FaTiktok />
          </a>
          <button
            onClick={() => toggleMenu()}
            className="text-2xl text-slate-900 md:hidden"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>
    </motion.nav>

    <AnimatePresence>
      {isMenuOpen && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          className="fixed right-0 top-0 z-50 h-full w-72 border-l border-white/20 bg-slate-950 p-6 text-white shadow-2xl"
        >
          <div className="mb-8 flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold">Menu</h2>
            <button onClick={() => toggleMenu(false)} aria-label="Close menu">
              <FaTimes />
            </button>
          </div>
          <div className="space-y-4">
            {navItems.map((item) => (
              <Link
                key={item}
                to={item.toLowerCase()}
                smooth={true}
                duration={500}
                offset={-80}
                className="block py-2 text-lg font-medium text-slate-100 transition-colors hover:text-cyan-300"
                onClick={() => toggleMenu(false)}
              >
                {item}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    {isMenuOpen && (
      <div
        className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm"
        onClick={() => toggleMenu(false)}
      />
    )}

    <header className="relative overflow-hidden px-4 pb-16 pt-28 md:px-8 md:pb-24 md:pt-36">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/70 bg-white/75 px-4 py-2 text-sm font-semibold text-cyan-900 shadow-[0_10px_40px_rgba(14,116,144,0.08)] backdrop-blur">
            Mobile apps and games. Senior-level execution.
          </div>
          <h1 className="mt-6 max-w-4xl font-display text-5xl font-bold leading-[0.95] tracking-tight text-slate-950 md:text-7xl">
            Mobile apps and games built with clarity, speed, and polish.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-700 md:text-xl">
            Appazzola designs and builds mobile apps and mobile games with a focused process, direct communication, and support through launch.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3.5 text-base font-semibold text-white shadow-[0_20px_60px_rgba(15,23,42,0.24)] transition-transform hover:-translate-y-0.5"
            >
              Start a Project
              <FaArrowRight className="text-sm" />
            </a>
            <a
              href="#projects"
              className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white/80 px-6 py-3.5 text-base font-semibold text-slate-900 backdrop-blur transition-colors hover:border-cyan-300 hover:text-cyan-800"
            >
              View Recent Work
            </a>
          </div>
        </motion.div>
      </div>
    </header>

    <main className="mx-auto max-w-6xl px-4 pb-20 md:px-8">
      <section className="mb-8 grid gap-4 md:mb-20 md:grid-cols-3">
        {quickPoints.map((item) => (
          <div
            key={item}
            className="surface-card p-5 text-sm font-medium leading-6 text-slate-700"
          >
            {item}
          </div>
        ))}
      </section>

      <section id="about" className="mb-20">
        <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <motion.img
            src="/owners.png"
            alt="Owners"
            className="surface-card w-full max-w-md justify-self-center rounded-[2rem] object-cover p-3"
            whileHover={{ scale: 1.02 }}
          />
          <div>
            <p className="section-eyebrow">About</p>
            <h2 className="section-title mt-3">
              A family-run studio built for direct collaboration.
            </h2>
            <p className="section-copy mt-6">
              Jarrod and Billie lead Appazzola as a hands-on team that blends technical delivery with practical marketing perspective. The goal is straightforward: build mobile products that feel intuitive and support real business or player outcomes.
            </p>
            <p className="section-copy mt-4">
              We prefer focused scopes, realistic store launches, and maintainable code over bloated retainers or overdesigned process.
            </p>
            <div className="mt-8 grid gap-3">
              {studioHighlights.map((highlight) => (
                <div
                  key={highlight}
                  className="surface-card px-5 py-4 text-base text-slate-700"
                >
                  {highlight}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="services" className="mb-20">
        <div>
          <p className="section-eyebrow">Services</p>
          <h2 className="section-title mt-3">
            Focused capabilities for mobile product teams and game founders.
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              className="surface-card p-7"
            >
              <h3
                className={`font-display font-bold leading-tight text-slate-950 ${
                  service.title === "Mobile Game Development"
                    ? "text-[1.28rem] md:text-[1.38rem]"
                    : "text-[1.45rem] md:text-[1.55rem]"
                }`}
              >
                {service.title}
              </h3>
              <p className="mt-3 text-base leading-7 text-slate-600">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="process" className="mb-20">
        <p className="section-eyebrow text-center">Process</p>
        <h2 className="section-title mx-auto mt-3 max-w-3xl text-center">
          Structured enough to stay aligned, lean enough to keep momentum.
        </h2>
        <p className="section-copy mx-auto mt-5 max-w-3xl text-center">
          Good delivery depends on fast feedback, a clear scope, and decisions that respect both the product and the release timeline.
        </p>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="surface-card p-8"
            >
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700">
                Step {index + 1}
              </p>
              <h3 className="mt-3 font-display text-2xl font-bold text-slate-950">
                {step.title}
              </h3>
              <p className="mt-4 text-slate-600">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="projects" className="mb-20">
        <p className="section-eyebrow">Projects</p>
        <h2 className="section-title mt-3">Selected work from the studio.</h2>
        <div className="mt-10 flex justify-center">
          <div className="grid max-w-4xl grid-cols-1 gap-8">
            {projectItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                className="surface-card overflow-hidden p-8 md:p-10"
              >
                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                  <div className="flex max-w-2xl flex-col gap-5 sm:flex-row sm:items-center">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.imageAlt || ""}
                        className={
                          item.imageClassName ||
                          "h-24 w-24 rounded-2xl object-cover shadow-lg ring-1 ring-slate-200"
                        }
                      />
                    )}
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700">
                        Featured launch
                      </p>
                      <h3 className="mt-3 font-display text-3xl font-bold text-slate-950">
                        {item.title}
                      </h3>
                      <p className="mt-4 text-lg leading-8 text-slate-700">{item.desc}</p>
                    </div>
                  </div>
                  <p className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">
                    {item.status}
                  </p>
                </div>
                <div className="mt-8 grid gap-3 text-slate-700">
                  {item.details?.map((detail) => (
                    <div key={detail} className="rounded-2xl bg-slate-50 px-4 py-4">
                      {detail}
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex flex-col space-y-3 md:flex-row md:space-x-4 md:space-y-0">
                  {item.links?.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.href}
                      target={link.href?.startsWith("http") ? "_blank" : undefined}
                      rel={link.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                      className={
                        link.variant === "primary"
                          ? "inline-flex min-h-14 items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-[0_14px_35px_rgba(15,23,42,0.22)] transition hover:-translate-y-0.5 hover:bg-cyan-800"
                          : "inline-flex min-h-14 items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-transform hover:-translate-y-0.5"
                      }
                    >
                      {link.img ? (
                        <img
                          src={link.img}
                          alt={link.alt}
                          className={`max-h-12 ${link.className || ""}`}
                        />
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          {link.label}
                          {link.variant === "primary" && (
                            <span aria-hidden="true" className="text-base leading-none">
                              →
                            </span>
                          )}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-20">
        <p className="section-eyebrow text-center">Testimonial</p>
        <h2 className="section-title mx-auto mt-3 max-w-3xl text-center">
          Client feedback from a shipped app.
        </h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="surface-card mx-auto mt-12 max-w-5xl overflow-hidden p-8 md:p-10"
        >
          <div className="grid items-center gap-8 md:grid-cols-[0.75fr_1.25fr]">
            <a
              href="https://news.thesunontheweb.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit The Sun Newspaper website"
              className="rounded-[1.75rem] bg-slate-950/4 p-6 transition hover:bg-slate-950/8"
            >
              <img
                src="/the-sun-logo.png"
                alt="The Sun Newspaper logo"
                className="mx-auto max-h-24 w-auto object-contain md:max-h-28"
              />
            </a>
            <div>
              <blockquote className="font-display text-2xl leading-10 tracking-tight text-slate-950 md:text-3xl">
                “{testimonial.quote}”
              </blockquote>
              <p className="mt-6 text-base font-semibold text-slate-950">
                {testimonial.author}
              </p>
              <p className="mt-1 text-sm uppercase tracking-[0.16em] text-slate-500">
                {testimonial.role}
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="mb-20">
        <p className="section-eyebrow text-center">FAQ</p>
        <h2 className="section-title mx-auto mt-3 max-w-3xl text-center">
          Common questions, answered directly.
        </h2>
        <p className="section-copy mx-auto mt-5 max-w-3xl text-center">
          These usually come up early in conversations with companies, founders, and product teams.
        </p>
        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
          {faqItems.map((item, index) => (
            <motion.div
              key={item.question}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="surface-card p-6"
            >
              <h3 className="font-display text-xl font-bold text-slate-950">
                {item.question}
              </h3>
              <p className="mt-3 leading-7 text-slate-600">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="contact" className="mb-20">
        <div className="overflow-hidden rounded-[2rem] bg-slate-950 px-6 py-10 text-white shadow-[0_30px_120px_rgba(15,23,42,0.28)] md:px-10 md:py-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
                Contact
              </p>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight md:text-5xl">
                Talk to us about your next app or game.
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                Reach out if you want us to design and build a mobile app or game, or maintain something we have already shipped together.
              </p>
            </div>
            <a
              href="mailto:contact@appazzola.com"
              className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3.5 text-base font-semibold text-slate-950 transition-transform hover:-translate-y-0.5"
            >
              contact@appazzola.com
            </a>
          </div>
        </div>
      </section>
    </main>

    <footer className="border-t border-white/60 px-4 py-10 md:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="flex items-center gap-0.5 font-display text-2xl font-bold text-slate-950">
            <img
              src="/appazzola-icon-256.png"
              alt="A"
              className="h-10 w-10 rounded-lg object-contain"
            />
            <span>ppazzola</span>
          </p>
          <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
            Mobile apps and games built with practical product thinking and direct collaboration.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            © 2026 Appazzola, LLC. All Rights Reserved.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm font-medium text-slate-600">
          <a href="/games/paw-merge-idle" className="hover:text-slate-950">
            Paw Merge Idle
          </a>
          <a href="/games/rascal-cat" className="hover:text-slate-950">
            Rascal Cat Game
          </a>
          <a href="/games/aether-dash" className="hover:text-slate-950">
            Aether Dash Game
          </a>
        </div>
      </div>
    </footer>

    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: scrollY > 300 ? 1 : 0 }}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-6 z-40 rounded-full bg-slate-950 px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white shadow-[0_20px_60px_rgba(15,23,42,0.24)] transition-colors hover:bg-cyan-800"
      aria-label="Back to top"
    >
      Top
    </motion.button>
  </div>
);

export default HomePage;
